import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  exportAccount,
  prepareAccountDeletion, confirmAccountDeletion, getDeletionControlStatus,
  getConfirmationEnvelope, advanceDeletionCapture, cancelAccountDeletion, confirmDestructiveDeletion,
} from "@/api";

// Customer-facing account-deletion flow. Uses the existing staged deletion architecture.
// Secrets (control credential / recovery code / server-issued consent values) live ONLY in refs and
// are NEVER rendered. No technical terminology is shown. With destructive features OFF, the first
// destructive step fails safely and the UI clearly reports it without simulating a deletion.

const featureOff = (e) => {
  const s = e?.response?.status;
  return s === 404 || s === 503;
};

export default function DeleteAccountFlow({ preflight, onCancel, onDeleted }) {
  const [stage, setStage] = useState("explain"); // explain | confirm | processing | unavailable | cancelled
  const [typed, setTyped] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  const [exportedOk, setExportedOk] = useState(false);

  const dccRef = useRef(null);
  const recoveryRef = useRef(null);   // held only if the contract returns it; never displayed
  const genRef = useRef(null);
  const pollRef = useRef(null);
  const destructiveTried = useRef(false);

  const canConfirmDeletion = preflight?.can_confirm_deletion !== false;
  const blockers = preflight?.blockers || [];

  const stopPoll = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);
  useEffect(() => () => stopPoll(), [stopPoll]);

  const toUnavailable = (msg) => { stopPoll(); setBusy(false); setMessage(msg); setStage("unavailable"); };

  // Final destructive progression — only reachable once the feature is enabled server-side.
  const attemptDestructive = useCallback(async (dcc) => {
    if (destructiveTried.current) return;
    destructiveTried.current = true;
    try {
      try { await advanceDeletionCapture(dcc); } catch (e) { /* may already be progressed */ }
      const env = await getConfirmationEnvelope(dcc);   // internal consent values — never shown
      await confirmDestructiveDeletion({
        dcc, expected_rev: env.expected_rev, snapshot_hash: env.snapshot_hash,
      });
      stopPoll();
      onDeleted();                                       // success -> logged-out
    } catch (e) {
      destructiveTried.current = false;                  // stay pending; never claim success
    }
  }, [onDeleted, stopPoll]);

  const startPolling = useCallback((dcc) => {
    const tick = async () => {
      try {
        const st = await getDeletionControlStatus(dcc);
        setStatus({ gate: st.gate, cancellation_available: st.cancellation_available,
                    action_required: st.action_required });
        if (st.gate === "ready") await attemptDestructive(dcc);
      } catch (e) { /* transient; keep pending */ }
    };
    tick();
    pollRef.current = setInterval(tick, 4000);
  }, [attemptDestructive]);

  const runDeletion = async () => {
    setBusy(true); setMessage("");
    let prep;
    try {
      prep = await prepareAccountDeletion();
    } catch (e) {
      if (featureOff(e)) return toUnavailable("Account deletion isn’t available right now. No changes were made to your account.");
      if (e?.response?.status === 429) return toUnavailable("Too many attempts. Please try again later. No changes were made to your account.");
      return toUnavailable("We couldn’t start account deletion right now. No changes were made to your account.");
    }
    dccRef.current = prep.dcc;
    recoveryRef.current = prep.recovery_credential || null;
    genRef.current = prep.deletion_request_generation;
    try {
      await confirmAccountDeletion(genRef.current, dccRef.current);
    } catch (e) {
      if (featureOff(e)) return toUnavailable("Account deletion isn’t available right now. No changes were made to your account.");
      return toUnavailable("We couldn’t complete your request. Please start again. No changes were made to your account.");
    }
    setBusy(false);
    setStage("processing");
    startPolling(dccRef.current);
  };

  const doCancel = async () => {
    setBusy(true);
    try { if (dccRef.current) await cancelAccountDeletion(dccRef.current); } catch (e) { /* best effort */ }
    stopPoll();
    dccRef.current = null; recoveryRef.current = null; genRef.current = null;
    setBusy(false);
    setStage("cancelled");
  };

  const typedOk = typed.trim() === "DELETE";

  // Optional export nudge — read-only GET; never required, never mutates deletion state.
  const handleExport = async () => {
    setExporting(true); setExportError(false); setExportedOk(false);
    try {
      const data = await exportAccount();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "findability-account-export.json";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportedOk(true);
    } catch (e) {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  };

  // ── Render ──
  if (stage === "explain") {
    return (
      <div data-testid="delete-flow-explain">
        <h3 className="acct-h3 acct-danger-title">Delete account</h3>
        <p className="acct-help">This permanently removes your account and all of your Findability work —
          your profile, cycles, efforts, saved posts, and findability checks.</p>
        <p className="acct-help">A limited set of payment and audit records is kept, in a minimized form no
          longer linked to your live account, where required by law and accounting rules.</p>
        {!canConfirmDeletion && blockers.length > 0 && (
          <div className="acct-block acct-block-block" data-testid="delete-flow-blocked">
            <div className="acct-block-title">Before you can delete</div>
            <ul className="acct-list">{blockers.map((b, i) => <li key={i}>{b}</li>)}</ul>
          </div>
        )}
        <div className="acct-export-nudge" data-testid="delete-export-nudge">
          <p className="acct-help" style={{ margin: 0 }}>Before you delete your account, you may want to
            download a copy of your data.</p>
          <button className="acct-btn acct-btn-ghost" onClick={handleExport} disabled={exporting}
                  data-testid="delete-export-btn">
            {exporting ? "Preparing…" : "Download my data"}
          </button>
          {exportedOk && (
            <p className="acct-note-ok" data-testid="delete-export-ok" style={{ marginTop: 8 }}>Your download has started.</p>
          )}
          {exportError && (
            <p className="acct-note-err" data-testid="delete-export-error" style={{ marginTop: 8 }}>
              We couldn't prepare your download just now.{" "}
              <button className="acct-link" onClick={handleExport} data-testid="delete-export-retry">Try again</button>
              {" "}You can still continue.
            </p>
          )}
        </div>
        <div className="acct-flow-actions">
          <button className="acct-btn acct-btn-ghost" onClick={onCancel} data-testid="delete-flow-keep">Keep my account</button>
          <button className="acct-btn acct-btn-danger-on" disabled={!canConfirmDeletion}
                  onClick={() => setStage("confirm")} data-testid="delete-flow-continue">Continue</button>
        </div>
      </div>
    );
  }

  if (stage === "confirm") {
    return (
      <div data-testid="delete-flow-confirm">
        <h3 className="acct-h3 acct-danger-title">This cannot be undone</h3>
        <p className="acct-help">To confirm, type <strong>DELETE</strong> below and tick the box.</p>
        <label className="acct-field-label" htmlFor="delete-type">Type DELETE to continue</label>
        <input id="delete-type" className="acct-input" value={typed} autoComplete="off"
               onChange={(e) => setTyped(e.target.value)} data-testid="delete-type-input" />
        <label className="acct-check-row">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
                 data-testid="delete-ack-checkbox" />
          <span>I understand this cannot be undone</span>
        </label>
        {message && <p className="acct-note-err" data-testid="delete-flow-error">{message}</p>}
        <div className="acct-flow-actions">
          <button className="acct-btn acct-btn-ghost" onClick={() => setStage("explain")}
                  disabled={busy} data-testid="delete-flow-back">Go back</button>
          <button className="acct-btn acct-btn-danger-on" disabled={!typedOk || !ack || busy}
                  onClick={runDeletion} data-testid="delete-confirm-btn">
            {busy ? "Please wait…" : "Delete my account"}
          </button>
        </div>
      </div>
    );
  }

  if (stage === "processing") {
    return (
      <div data-testid="delete-flow-processing">
        <h3 className="acct-h3">Deletion is being processed</h3>
        <p className="acct-help">Your account is scheduled for deletion. You can still cancel while it is
          being processed.</p>
        <div className="acct-spinner" aria-hidden="true"><span /><span /><span /></div>
        {status?.action_required === "entitlement_action_required" && (
          <p className="acct-help" data-testid="delete-flow-action-required">There is one item on your
            account to resolve before deletion can finish.</p>
        )}
        {status?.cancellation_available !== false && (
          <div className="acct-flow-actions">
            <button className="acct-btn acct-btn-primary" onClick={doCancel} disabled={busy}
                    data-testid="delete-cancel-processing">
              {busy ? "Cancelling…" : "Cancel deletion"}
            </button>
          </div>
        )}
      </div>
    );
  }

  if (stage === "unavailable") {
    return (
      <div data-testid="delete-flow-unavailable">
        <h3 className="acct-h3">Not available right now</h3>
        <p className="acct-help" data-testid="delete-flow-unavailable-msg">{message}</p>
        <div className="acct-flow-actions">
          <button className="acct-btn acct-btn-ghost" onClick={onCancel} data-testid="delete-flow-unavailable-back">Back to account</button>
        </div>
      </div>
    );
  }

  // cancelled
  return (
    <div data-testid="delete-flow-cancelled">
      <h3 className="acct-h3">Your account was not deleted</h3>
      <p className="acct-help">Your account and all of your Findability work are safe.</p>
      <div className="acct-flow-actions">
        <button className="acct-btn acct-btn-ghost" onClick={onCancel} data-testid="delete-flow-cancelled-back">Back to account</button>
      </div>
    </div>
  );
}
